import React from 'react';
import SEO from '../components/SEO';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, Target, Heart, Sparkles, Shield, Award, Lightbulb, Building, Sun, Battery, Flame, Wind, Plug, CheckCircle } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CTASection from '../components/home/CTASection';
import { Button } from '../components/ui/button';
import { companyInfo } from '../data/mock';

const AboutPage = () => {
  const values = [
    {
      icon: Shield,
      title: "Integrity",
      description: "Honest advice, no pushy sales tactics."
    },
    {
      icon: Award,
      title: "Excellence",
      description: "Meticulous installs, premium parts, relentless after-sales support."
    },
    {
      icon: Lightbulb,
      title: "Innovation",
      description: "AI-powered quoting, battery-ready designs, future-proof thinking."
    },
    {
      icon: Building,
      title: "Community",
      description: "Local apprenticeships, regional sponsorships, open-door education."
    }
  ];

  const whyChooseUs = [
    {
      title: "Proven Heritage",
      description: "First established in 2015 as Supa Solar, now celebrating 10+ years of energy expertise."
    },
    {
      title: "Whole-of-Home Vision",
      description: "Integrated packages so every technology works harder together."
    },
    {
      title: "Certified Quality",
      description: "Tier-1 components, neat workmanship, 10-year installation warranty."
    },
    {
      title: "Transparent Pricing",
      description: "Real numbers up-front, rebates calculated, no hidden surprises."
    },
    {
      title: "Optional Supa Care",
      description: "Annual performance checks, bill reviews, and retailer-switch guidance."
    }
  ];

  const solutions = [
    { icon: Sun, name: "Solar PV", benefit: "Generate up to 80% of daytime power straight from your roof." },
    { icon: Battery, name: "Battery Storage", benefit: "Store excess solar, ride through blackouts, and export when prices spike." },
    { icon: Flame, name: "Heat Pump Hot Water", benefit: "Up to 70% cheaper hot water than electric resistance." },
    { icon: Wind, name: "Heating & Cooling", benefit: "Year-round comfort at about half the running cost of old systems." },
    { icon: Plug, name: "EV Chargers", benefit: "Home charging that's up to five times cheaper than petrol." }
  ];

  const whoWeAre = [
    {
      title: "NETCC-Approved Seller & Solar Victoria Approved Retailer",
      description: "Your guarantee of ethical sales, quality products, and protected consumer rights."
    },
    {
      title: "Seasoned Crew",
      description: "Guided by an operations team with 14 years of solar-industry know-how and a network of CEC-accredited electricians across VIC, NSW & QLD."
    },
    {
      title: "Customer-First Culture",
      description: "Guided by our Customer Service and Customer Care & Support policies."
    },
    {
      title: "Smart Tech Meets Human Care",
      description: "Wattson, our AI energy assistant, delivers instant ball-park quotes while real humans handle the details."
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="About Us — 10+ Years of Australian Energy Expertise"
        description="Supa Energy Hub has been delivering smarter home and business energy solutions across Australia for over a decade. Honest advice, quality installs, and the Supa Promise."
      />
      <Header />
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-16 hero-gradient relative overflow-hidden">
          <div className="absolute top-20 right-10 w-72 h-72 bg-[#B5D43B]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#3D5A47]/5 rounded-full blur-3xl" />
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-block text-[#B5D43B] font-semibold text-sm tracking-wider uppercase mb-4">
                  About Us
                </span>
                <h1 className="heading-1 mb-6">
                  We Help You Power Your Home with the{' '}
                  <span className="text-[#B5D43B]">Sun</span>
                </h1>
                <p className="body-large mb-8">
                  Make the eco-friendly choice and start powering your home with clean, renewable energy today.
                </p>
                <Link to="/contact">
                  <Button className="btn-primary gap-2">
                    Get Started
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
              <div className="flex justify-center lg:justify-end">
                <img 
                  src="https://customer-assets.emergentagent.com/job_greenenergy-site/artifacts/hrbbyrl5_10%2B%20years%20of%20Supa.png"
                  alt="10+ Years of Energy Expertise"
                  className="w-full max-w-md rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Our Story Section */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <h2 className="heading-2 text-center mb-8">Our Story</h2>
              <div className="space-y-6 text-gray-600 leading-relaxed text-lg">
                <p>
                  <strong className="text-[#3D5A47]">10 years strong.</strong> Since June 2015, the Supa journey has had one clear mission: help Aussie households harness the sun and cut their power bills. Over the next decade we installed thousands of systems, earned wall-to-wall ★★★★★ reviews, and built a reputation for straight-talking advice and meticulous workmanship.
                </p>
                <p>
                  By 2024 our customers were asking for more than solar alone, so we expanded into batteries, heat-pump hot water, efficient heating & cooling, and EV charging. In 2025 we stepped confidently into the future as <strong className="text-[#3D5A47]">Supa Energy Hub</strong>—the same passionate team with a bigger toolkit to power your whole home.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Who We Are Today */}
        <section className="py-16 bg-gradient-to-br from-[#f8faf5] to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="heading-2 text-center mb-12">Who We Are Today</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {whoWeAre.map((item, index) => (
                <div key={index} className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#B5D43B]/15 flex items-center justify-center flex-shrink-0 mt-1">
                      <CheckCircle className="w-4 h-4 text-[#3D5A47]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#3D5A47] mb-2">{item.title}</h3>
                      <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What We Do Section */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="heading-2 text-center mb-4">What We Do</h2>
            <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
              Comprehensive energy solutions to power your whole home efficiently.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {solutions.map((solution, index) => (
                <div key={index} className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-shadow">
                  <div className="w-12 h-12 rounded-xl bg-[#B5D43B]/15 flex items-center justify-center mb-4">
                    <solution.icon className="w-6 h-6 text-[#3D5A47]" />
                  </div>
                  <h3 className="font-semibold text-[#3D5A47] mb-2">{solution.name}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{solution.benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="py-16 bg-[#3D5A47]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="heading-2 text-center text-white mb-12">Why Choose Supa Energy Hub?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {whyChooseUs.map((item, index) => (
                <div key={index} className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl border border-white/20">
                  <h3 className="font-semibold text-[#B5D43B] mb-2">{item.title}</h3>
                  <p className="text-white/80 text-sm leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <h2 className="heading-2 mb-4">Our Values</h2>
              <p className="text-gray-600">
                These principles guide everything we do.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((value, index) => (
                <div key={index} className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-shadow text-center">
                  <div className="w-14 h-14 rounded-2xl bg-[#B5D43B]/15 flex items-center justify-center mx-auto mb-4">
                    <value.icon className="w-7 h-7 text-[#3D5A47]" />
                  </div>
                  <h3 className="font-semibold text-[#3D5A47] mb-2">{value.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Looking Forward Section */}
        <section className="py-16 bg-gradient-to-br from-[#f8faf5] to-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="heading-2 mb-6">Looking Forward</h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              Whether you first met us as <strong className="text-[#3D5A47]">Supa Solar</strong> or you're discovering <strong className="text-[#3D5A47]">Supa Energy Hub</strong> for the first time, our promise remains the same: <span className="text-[#B5D43B] font-semibold">exceptional service, quality installations, and genuine lifelong support</span>. Ready to future-proof your home and pocket?
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:0478453548">
                <Button className="btn-secondary gap-2">
                  📞 0478 453 548
                </Button>
              </a>
              <a href="mailto:hello@supaenergyhub.com.au">
                <Button className="btn-primary gap-2">
                  ✉️ hello@supaenergyhub.com.au
                </Button>
              </a>
            </div>
          </div>
        </section>

        <CTASection />
      </main>
      <Footer />
    </div>
  );
};

export default AboutPage;
